import { useContext, useEffect, useRef, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import { empty, prepareResponseData } from "../../Utilities/utils";

// css
import "../schools/Schools.css";

// api
import schoolApi from "../../api/School";
import paymentApi from "../../api/Payment";

// data
import { Status } from "../../data/Status";

//components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import AppWrapper from "../../components/appWrapper/AppWrapper";
import InputField from "../../components/form/InputField";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import SelectField from "../../components/form/SelectField";
import Underline from "../../components/others/Underline";
import { AuthContext } from "../Root/ProtectedRoute";
import { Toast } from "primereact/toast";
import FullPageLoader from "../../components/loader/FullPageLoader";
import EmptyDiv from "../../components/emptyDiv/EmptyDiv";

const required = "This field is required.";
const validationSchema = Yup.object().shape({
  school_id: Yup.string().required(required),
  amount: Yup.string().required(required),
  discount: Yup.string().required(required),
  charge_rate: Yup.string().min(3).max(100).required(required),
  status: Yup.string().min(2).max(10).required(required),
});

const initialValues = {
  school_id: "",
  amount: "",
  charge_rate: "",
  status: "",
};

const AddSchoolPayment = ({ ...props }) => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [schools, setSchools] = useState([]);
  const toastTR = useRef(null);

  useEffect(() => {
    getSchools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //alert functions
  const responseDailog = (severity = null, summary = null, detail = null) => {
    toastTR.current.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  const getSchools = async () => {
    try {
      if (!isLoading) setIsLoading(true);
      const response = await schoolApi.getAllSchools();
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data?.response) {
        setSchools([]);
        return responseDailog(
          "error",
          "Error Alert",
          !empty(response_data.response)
            ? typeof response_data.response === "string"
              ? response_data.response
              : "Something went wrong!"
            : "Failed to fetch school details",
        );
      }

      if (!empty(response_data) && !empty(response_data.response)) {
        const schools = response_data.response;
        setSchools(schools);
      }
    } catch (error) {
      responseDailog("error", "Internal Error", "Something went wrong.");
    } finally {
      setIsLoading(false);
    }
  };

  // get school details
  const getSchoolDetails = async (schoolId) => {
    try {
      if (!isLoading) setIsLoading(true);
      const response = await schoolApi.getSingleSchool(schoolId);
      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data?.response) {
        return 0;
      }

      if (!empty(response_data) && !empty(response_data.response)) {
        const schools = response_data.response;
        const charge_rate =
          !empty(schools) && !empty(schools.chargeRate)
            ? schools.chargeRate
            : 0;
        return charge_rate;
      }
    } catch (error) {
      return 0;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    if (!isLoading) setIsLoading(true);
    try {
      const adminId = !empty(user) && !empty(user._id) ? user._id : "";
      const fields = [
        "school_id",
        "amount",
        "charge_rate",
        "status",
        "discount",
      ];
      const schoolDetails = {};
      if (!empty(values)) {
        _.forEach(fields, (data) => {
          schoolDetails[data] =
            !empty(data) && !empty(values[data]) ? values[data] : "";
        });
      }
      let response;
      response = await paymentApi.addPayment(
        adminId,
        ...Object.values(schoolDetails),
      );

      const response_data = prepareResponseData(response);
      if (empty(response_data) || !response_data.success) {
        if (
          !empty(response_data) &&
          !empty(response_data.statusCodeType) &&
          response_data.statusCodeType === "unauthorized"
        ) {
          return navigate("/");
        }
        const error_response = !empty(response_data.response)
          ? response_data.response
          : "Something went wrong!";
        return responseDailog("error", "Error Alert!", error_response);
      }

      const data =
        !empty(response_data) && !empty(response_data.response)
          ? response_data.response
          : {};
      let _paymentId = "";
      if (!empty(data)) {
        _paymentId = !empty(data.schoolId) ? data.schoolId : "";
        return navigate(`/school/payment/${_paymentId}/receipt`, {
          state: {
            paymentAdded: true,
          },
        });
      }

      navigate(`/school/payments`, {
        state: { paymentAdded: true },
      });
    } catch (error) {
      return responseDailog(
        "error",
        "Internal Error!",
        "Something went wrong.",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleSchoolChange = (setFieldValue) => async (e) => {
    const schoolId = e.target.value;
    const charge_rate = await getSchoolDetails(schoolId);
    setFieldValue("school_id", schoolId);
    setFieldValue("charge_rate", charge_rate);
  };

  return (
    <>
      <AppWrapper {...props}>
        <main>
          <div className="container flex-center-top">
            {/*  header start */}
            <MainHeader title="Add School" />
            {/* end of header */}

            <div className="form-container mt-30">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ handleSubmit, values, handleChange, setFieldValue }) => (
                  <Form style={{ width: "100%" }}>
                    <div>
                      <em>
                        <strong>Note: *</strong> Please enter the correct
                        details for this payment
                      </em>
                    </div>
                    <div
                      style={{ marginTop: 30, fontSize: 18, color: "#633ccd" }}
                    >
                      Payment Details
                      <Underline />
                    </div>
                    <div
                      style={{ width: "100%", display: "flex", columnGap: 12 }}
                    >
                      <SelectField
                        labelTitle={
                          <>
                            School
                            <span className="required">*</span>
                          </>
                        }
                        placeholder="Select School"
                        name="school_id"
                        options={schools}
                        height={50}
                        display="name"
                        valueKey="id"
                        selectedOption={values.school_id}
                        handleChangeFunc={handleSchoolChange(setFieldValue)}
                      />
                    </div>
                    <div
                      style={{ width: "100%", display: "flex", columnGap: 12 }}
                    >
                      <InputField
                        placeholder="Enter Amount"
                        name="amount"
                        height={50}
                        labelTitle={
                          <>
                            Amount <span className="required">*</span>
                          </>
                        }
                      />
                      <InputField
                        placeholder="Enter Charge Rate"
                        name="charge_rate"
                        height={50}
                        labelTitle={
                          <>
                            Charge Rate <span className="required">*</span>
                          </>
                        }
                      />
                      <InputField
                        placeholder="Enter Discount (# of students)"
                        name="discount"
                        height={50}
                        labelTitle="Discount"
                      />
                    </div>
                    <div
                      style={{ width: "100%", display: "flex", columnGap: 12 }}
                    >
                      <SelectField
                        labelTitle={
                          <>
                            Status
                            <span className="required">*</span>
                          </>
                        }
                        placeholder="Select Status"
                        name="status"
                        options={Status}
                        height={50}
                        valueKey="title"
                        selectedOption={values.status}
                        handleChangeFunc={handleChange}
                      />
                      <EmptyDiv />
                      <EmptyDiv />
                      <EmptyDiv />
                    </div>
                    <div
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      }}
                    >
                      <ButtonIcon
                        height={45}
                        marginTop={5}
                        color="#ffffff"
                        backgroundColor="#633ccd"
                        width={300}
                        borderColor="#633ccd"
                        buttonText="Add Payment"
                        type="submit"
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          </div>
        </main>
        {isLoading && <FullPageLoader visible={isLoading} />}
        <Toast ref={toastTR} position="bottom-left" />
      </AppWrapper>
    </>
  );
};

export default AddSchoolPayment;
